import type { HttpError } from '@refinedev/core'
import { useMany, useSelect } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { Cabinet, Location } from '@sirius/contracts/admin'
import type { Column, ColumnDef, Table } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import { Check } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ListEmptyState } from '~/components/listEmptyState'
import { EditButton } from '~/components/refineUi/buttons/edit'
import { ShowButton } from '~/components/refineUi/buttons/show'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import {
  DataTableFilterCombobox,
  DataTableFilterDropdown,
  DataTableFilterDropdownText,
} from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { Button } from '~/components/ui/button'
import { Separator } from '~/components/ui/separator'
import { formatDateTime } from '~/i18n/format'
import {
  LOCATION_FIELD,
  STORAGE,
  locationColumnFilter,
  locationFilterValue,
  statusLabel,
} from './cabinetStatus'
import type { GameNames } from './gameNames'
import { gameNamesText } from './gameNames'
import { CabinetStatusAction, CabinetStatusBadge } from './statusActions'
import { useGameOptions } from './useGameOptions'

const dateTime = (value: Date) => formatDateTime(value)

const statusOptions = (t: TFunction) =>
  (['active', 'disabled', 'decommissioned'] as const).map((status) => ({
    label: statusLabel(status, t),
    value: status,
  }))

type Option = { label: string; value: string }

/** Location names of the current page (`useMany`), for the location cells. */
type LocationNames = { byId: ReadonlyMap<string, string>; isError: boolean }
const LocationNamesContext = createContext<LocationNames>({
  byId: new Map(),
  isError: false,
})

function LocationCell({ locationId }: { locationId: string | null }) {
  const { t } = useTranslation()
  const { byId, isError } = useContext(LocationNamesContext)
  if (locationId === null) {
    return <>{t('cabinetsPage.common.inStorage')}</>
  }
  // a failed lookup shows a dash, never a wrong or missing-looking name
  return <>{byId.get(locationId) ?? (isError ? '—' : '…')}</>
}

/** Every game's name (`gameOptions`, one query), for the games cells. */
const GameNamesContext = createContext<GameNames>({
  byId: new Map(),
  isLoading: true,
  isError: false,
})

function GamesCell({ gameIds }: { gameIds: readonly string[] }) {
  return <>{gameNamesText(gameIds, useContext(GameNamesContext))}</>
}

/**
 * Location filter: "In storage" is the `null` operator, a location `eq`. The
 * operator rides on the column filter, which `@refinedev/react-table` turns
 * into the Refine filter (and back from the URL).
 */
function LocationFilter({
  column,
  table,
  options,
}: {
  column: Column<Cabinet>
  table: Table<Cabinet>
  options: Option[]
}) {
  const { t } = useTranslation()
  const selected = locationFilterValue(table.getState().columnFilters)
  const select = (value: string | undefined) =>
    table.setColumnFilters((filters) => [
      ...filters.filter(({ id }) => id !== LOCATION_FIELD),
      ...(value === undefined ? [] : [locationColumnFilter(value)]),
    ])

  return (
    <DataTableFilterDropdown column={column}>
      {({ setIsOpen }) => (
        <div className="flex min-w-48 flex-col gap-1">
          {options.map((option) => (
            <Button
              key={option.value}
              variant="ghost"
              size="sm"
              className="justify-between"
              onClick={() => {
                select(option.value)
                setIsOpen(false)
              }}
            >
              {option.label}
              {selected === option.value && <Check />}
            </Button>
          ))}
          <Separator />
          <Button
            variant="ghost"
            size="sm"
            disabled={selected === undefined}
            onClick={() => {
              select(undefined)
              setIsOpen(false)
            }}
          >
            {t('cabinetsPage.list.clear')}
          </Button>
        </div>
      )}
    </DataTableFilterDropdown>
  )
}

export function CabinetList() {
  const { t } = useTranslation()
  const locationOptions = useSelect<Location, HttpError>({
    resource: 'locations',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })
  const filterOptions = useMemo<Option[]>(
    () => [
      { label: t('cabinetsPage.common.inStorage'), value: STORAGE },
      ...locationOptions.options.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    ],
    [locationOptions.options, t],
  )

  const columns = useMemo<ColumnDef<Cabinet>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('cabinetsPage.list.name')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('cabinetsPage.list.searchName')}
            />
          </div>
        ),
      },
      {
        id: LOCATION_FIELD,
        accessorKey: LOCATION_FIELD,
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('cabinetsPage.list.location')}
            <LocationFilter
              column={column}
              table={table}
              options={filterOptions}
            />
          </div>
        ),
        cell: ({ getValue }) => (
          <LocationCell locationId={getValue<string | null>()} />
        ),
        enableSorting: false,
      },
      {
        id: 'gameIds',
        accessorKey: 'gameIds',
        header: t('cabinetsPage.list.games'),
        cell: ({ getValue }) => <GamesCell gameIds={getValue<string[]>()} />,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('cabinetsPage.list.status')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={statusOptions(t)}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ getValue }) => (
          <CabinetStatusBadge status={getValue<Cabinet['status']>()} />
        ),
        meta: { filterOperator: 'in' },
        enableSorting: false,
      },
      {
        id: 'lastSeenAt',
        accessorKey: 'lastSeenAt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('cabinetsPage.list.lastSeen')}
            <DataTableSorter column={column} />
          </div>
        ),
        cell: ({ getValue }) => {
          const lastSeenAt = getValue<Date | null>()
          return lastSeenAt === null
            ? t('cabinetsPage.common.never')
            : dateTime(lastSeenAt)
        },
        enableColumnFilter: false,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('cabinetsPage.list.created')}
            <DataTableSorter column={column} />
          </div>
        ),
        cell: ({ getValue }) => dateTime(getValue<Date>()),
        enableColumnFilter: false,
      },
      {
        id: 'actions',
        header: '',
        // Show, Edit and the status action side by side
        size: 290,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <ShowButton
              recordItemId={row.original.id}
              size="sm"
            />
            <EditButton
              recordItemId={row.original.id}
              size="sm"
            />
            <CabinetStatusAction cabinet={row.original} />
          </div>
        ),
      },
    ],
    [filterOptions, t],
  )

  const table = useTable<Cabinet, HttpError>({
    columns,
    refineCoreProps: {
      resource: 'cabinets',
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      sorters: { initial: [{ field: 'name', order: 'asc' }] },
    },
  })
  const { tableQuery, filters } = table.refineCore
  const isEmpty = tableQuery.data?.total === 0

  const locationIds = useMemo(
    () => [
      ...new Set(
        (tableQuery.data?.data ?? []).flatMap(({ locationId }) =>
          locationId === null ? [] : [locationId],
        ),
      ),
    ],
    [tableQuery.data],
  )
  const locations = useMany<Location, HttpError>({
    resource: 'locations',
    ids: locationIds,
    queryOptions: { enabled: locationIds.length > 0 },
  })
  const locationNames = useMemo<LocationNames>(
    () => ({
      byId: new Map(
        locations.result.data.map((location) => [location.id, location.name]),
      ),
      isError: locations.query.isError,
    }),
    [locations.result.data, locations.query.isError],
  )
  const { names: gameNames } = useGameOptions()

  return (
    <ListView>
      <ListViewHeader />
      {isEmpty ? (
        <ListEmptyState
          resource="cabinets"
          emptyText={t('cabinetsPage.list.empty')}
          noMatchText={t('cabinetsPage.list.noMatch')}
          hasFilters={filters.length > 0}
          // via the column filters, so react-table and Refine stay in sync
          onClearFilters={() => table.reactTable.setColumnFilters([])}
        />
      ) : (
        <LocationNamesContext value={locationNames}>
          <GameNamesContext value={gameNames}>
            <DataTable table={table} />
          </GameNamesContext>
        </LocationNamesContext>
      )}
    </ListView>
  )
}
